import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UsersUpdateSettingsRequest } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { userEnvSettingsKeys, type UserEnvSettings } from './entity';
import type { UserEnvSettingsScope } from './queries';

export interface UpdateUserEnvSettingsInput extends UserEnvSettingsScope {
  /**
   * Partial body: the endpoint applies only the settings the body carries, so
   * e.g. `{ people_page: { columns } }` updates that setting alone.
   */
  settings: UsersUpdateSettingsRequest;
}

/** @public */
export async function updateUserEnvSettings({
  accountId,
  userId,
  workspaceId,
  settings,
}: UpdateUserEnvSettingsInput): Promise<void> {
  await hydraUiApi.users.updateSettingsByEnv({
    params: {
      path: { account_id: String(accountId), id: String(userId), env_id: String(workspaceId) },
    },
    body: settings,
  });
}

function mergeCampaignsPage(
  previous: UserEnvSettings['campaigns_page'],
  update: UsersUpdateSettingsRequest['campaigns_page'],
): UserEnvSettings['campaigns_page'] {
  return {
    ...previous,
    columns: update?.columns ?? previous.columns,
    sort: update?.sort ?? previous.sort,
    preset_range: update?.preset_range ?? previous.preset_range,
  };
}

function mergeObjectsPage(
  previous: UserEnvSettings['objects_page'],
  update: UsersUpdateSettingsRequest['objects_page'],
): UserEnvSettings['objects_page'] {
  return {
    objectTypeColumns: update?.objectTypeColumns ?? previous.objectTypeColumns,
  };
}

/** Field-sparse merge, mirroring the endpoint: a carried field replaces its value, an omitted one is untouched. */
function mergeSettings(previous: UserEnvSettings, update: UsersUpdateSettingsRequest): UserEnvSettings {
  return {
    segment_ids: update.segment_ids ?? previous.segment_ids,
    delivery: { channels: update.delivery?.channels ?? previous.delivery.channels },
    performance: { channels: update.performance?.channels ?? previous.performance.channels },
    campaigns_page: mergeCampaignsPage(previous.campaigns_page, update.campaigns_page),
    people_page: { columns: update.people_page?.columns ?? previous.people_page.columns },
    objects_page: mergeObjectsPage(previous.objects_page, update.objects_page),
  };
}

interface UseUpdateUserEnvSettingsOptions {
  /**
   * TanStack mutation scope — pass one id per settings target to run updates
   * serially, so overlapping saves can't land on the server out of order.
   */
  scope?: { id: string };
  /** Runs even if the calling component unmounted mid-save. */
  onError?: (error: Error, variables: UpdateUserEnvSettingsInput) => void;
}

/**
 * The PUT returns 204, so on success the applied fields are merged into the
 * cached read directly — other readers of the settings blob (e.g. the CSV
 * export's "Displayed attributes") see the update without a refetch.
 *
 * @public
 */
export function useUpdateUserEnvSettings({ scope, onError }: UseUpdateUserEnvSettingsOptions = {}) {
  const qc = useQueryClient();
  return useMutation({
    scope,
    mutationFn: updateUserEnvSettings,
    onSuccess: (_data, variables) => {
      qc.setQueryData<UserEnvSettings>(
        userEnvSettingsKeys.detail(variables.accountId, variables.userId, variables.workspaceId),
        (previous) => (previous == null ? previous : mergeSettings(previous, variables.settings)),
      );
    },
    onError,
  });
}
